/**
 * Koblingen mellom to hus — festivalen og bookingbyrået (migrering 0084).
 *
 * ---------------------------------------------------------------------------------------------
 * Vedtak 93 og 99, 8. september. Et bookingbyrå er en EGEN organisasjon, og de to kobles ved at
 * festivalen inviterer på e-post og byrået godtar. Ingen katalog å vedlikeholde, og ingen blir
 * koblet til noen uten å vite det.
 *
 * ---------------------------------------------------------------------------------------------
 * DTO-EN VISER DEN ENE SIDENS RAD, og det er hele modellen.
 *
 * Koblingen er to rader, én i hvert hus, som peker på hverandre — se `organization-links.ts` i
 * skjemaet for hvorfor. Klienten ser bare sin egen, og `counterpartyName` er en KOPI tatt ved
 * godkjenning: motpartens virkelige navn bor hos dem, og RLS slipper oss ikke inn for å lese det.
 * En kopi som er en dag gammel er riktig svar her; alternativet er ingen liste i det hele tatt.
 */
import { z } from 'zod';

import { organizationKindSchema } from '../enums.js';

/** «Invitert» er sendt og ubesvart. «Trukket» er festivalens egen anger, «avslått» byråets. */
export const ORGANIZATION_LINK_STATUSES = ['invited', 'accepted', 'declined', 'revoked'] as const;
export type OrganizationLinkStatus = (typeof ORGANIZATION_LINK_STATUSES)[number];
export const organizationLinkStatusSchema = z
  .enum(ORGANIZATION_LINK_STATUSES)
  .meta({ id: 'OrganizationLinkStatus' });

export const organizationLinkDtoSchema = z
  .object({
    id: z.uuid(),
    status: organizationLinkStatusSchema,
    /** Byrået i VÅRT register denne koblingen gjelder. Null på byråets egen rad. */
    bookingAgencyId: z.uuid().nullable(),
    bookingAgencyName: z.string().nullable(),
    /** Motparten, når den finnes. Null mens invitasjonen står ute. */
    counterpartyOrganizationId: z.uuid().nullable(),
    counterpartyName: z.string().nullable(),
    counterpartyKind: organizationKindSchema.nullable(),
    /** Adressen invitasjonen gikk til. Blir stående etterpå: den er svaret på «hvem godtok dette». */
    invitedEmail: z.email().nullable(),
    expiresAt: z.iso.datetime().nullable(),
    acceptedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
  })
  .meta({ id: 'OrganizationLinkDto' });
export type OrganizationLinkDto = z.infer<typeof organizationLinkDtoSchema>;

/** Ikke paget: en organisasjon har titalls koblinger, ikke tusener, og lista leses hel. */
export const organizationLinkListResponseSchema = z
  .object({ items: z.array(organizationLinkDtoSchema) })
  .meta({ id: 'OrganizationLinkListResponse' });
export type OrganizationLinkListResponse = z.infer<typeof organizationLinkListResponseSchema>;

/**
 * Invitasjonen.
 *
 * `bookingAgencyId` er påkrevd: en festival inviterer et byrå den allerede har i registeret sitt,
 * ikke en adresse i løse lufta. Det er også det som gjør at svaret kan kobles tilbake til de 741
 * artistene som ligger hos nettopp det byrået.
 */
export const inviteOrganizationLinkRequestSchema = z
  .object({
    bookingAgencyId: z.uuid(),
    email: z.email().max(320),
    /** Med i e-posten. En invitasjon uten et ord fra avsenderen ser ut som spam. */
    message: z.string().trim().min(1).max(2000).nullish(),
  })
  .meta({ id: 'InviteOrganizationLinkRequest' });
export type InviteOrganizationLinkRequest = z.infer<typeof inviteOrganizationLinkRequestSchema>;

/**
 * Svaret på en invitasjon, og det bærer det rå TOKENET — én gang.
 *
 * Basen holder bare hashen (`organization_links.token_hash`), så dette er den ENESTE gangen den
 * strengen finnes utenfor mottakerens innboks. Skjermen må derfor vise lenken og si at den ikke
 * kommer igjen; en «kopier»-knapp uten den setningen er en felle.
 *
 * Den er en egen kontrakt framfor et anonymt objekt fordi den er et svar en klient parser
 * (prinsipp 3): et endepunkt som returnerer en form ingen har skrevet ned, er et endepunkt som
 * kan endre form uten at noe sier fra.
 */
export const inviteOrganizationLinkResponseSchema = z
  .object({
    link: organizationLinkDtoSchema,
    /** Rått, ikke hashet. Vises én gang og lagres aldri av klienten. */
    token: z.string(),
  })
  .meta({ id: 'InviteOrganizationLinkResponse' });
export type InviteOrganizationLinkResponse = z.infer<typeof inviteOrganizationLinkResponseSchema>;

/**
 * Innløsningen, sett fra byrået.
 *
 * Tokenet er det eneste som identifiserer koblingen — den som løser inn er per definisjon ikke i
 * festivalens organisasjon, så ingen id fra deres side ville vært lesbar. Byrået må være logget inn
 * og ha valgt hvilket av sine hus koblingen skal gjelde; det er `X-Tvitvi-Organization` som sier det.
 */
export const acceptOrganizationLinkRequestSchema = z
  .object({ token: z.string().min(20).max(200) })
  .meta({ id: 'AcceptOrganizationLinkRequest' });
export type AcceptOrganizationLinkRequest = z.infer<typeof acceptOrganizationLinkRequestSchema>;

/**
 * Det den som klikker lenken får se FØR de logger inn.
 *
 * Med vilje magert: hvem som inviterer og til hva. En invitasjon er en offentlig adresse noen kan
 * gjette på, og alt som står her lekker til den som gjetter riktig.
 */
export const organizationLinkPreviewSchema = z
  .object({
    organizationName: z.string(),
    invitedEmail: z.email().nullable(),
    expiresAt: z.iso.datetime().nullable(),
    /** Sann bare når invitasjonen fortsatt kan godtas. */
    open: z.boolean(),
  })
  .meta({ id: 'OrganizationLinkPreview' });
export type OrganizationLinkPreview = z.infer<typeof organizationLinkPreviewSchema>;
